import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type Ref,
} from "react";
import { Inbox } from "lucide-react";

import { useIsMobile } from "../../use-mobile";
import { Button } from "../../inputs/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../primitives/sheet";
import { cn } from "../../utils";
import { INBOX_LAYOUT_ROW_KEYBOARD_SCOPE } from "./inbox-layout";

// ---------------------------------------------------------------------------
// The mail inbox shell: a left rail, and a main column that shows either a
// scrollable list of rows or one row's detail.
//
// Extracted from `/emails` (`apps/web/src/routes/email-inbox-page.tsx`) so the
// super-admin remote mailbox browser is the SAME inbox, just backed by a
// different provider, rather than a lookalike built from a data grid. Both
// surfaces render these components; neither owns the layout any more.
//
// Slot-based on purpose. This shell has no opinion about what a row is, where
// items come from, or how selection is stored — `/emails` pages threads from
// an infinite query with URL-backed selection, and the remote browser pages
// provider messages behind a cursor it must not outlive. Every one of those is
// a difference in the DATA, so the shell takes rendered nodes and keeps out of
// it. That is also the difference from {@link InboxLayout} next door, which is
// the opposite trade: it owns items/selection/keyboard-nav and renders rail =
// the list itself, for surfaces that want a two-pane list+detail rather than a
// navigation rail beside a list.
//
// The one thing the shell DOES own is the scroll boundary. A list column that
// forgets `min-h-0` + `overflow-y-auto` inside a flex parent does not scroll,
// it silently clips — which is exactly what happened when the remote browser
// hand-rolled this. Owning it here means no surface can get it wrong again.
//
// It also owns the narrow-viewport fold. Below the `md` breakpoint a fixed
// rail beside a list leaves a phone with nothing to read, so the shell moves
// the rail into a left sheet and `InboxListHeader` grows the button that
// opens it — every surface gets the same behaviour without knowing about it.
// ---------------------------------------------------------------------------

type InboxShellContextValue = {
  /** True when the rail lives in a sheet rather than beside the list. */
  railInSheet: boolean;
  /** True for content rendered inside the rail itself (so, inside the sheet). */
  inRail: boolean;
  railLabel: string;
  openRail: () => void;
  /** Called by a mounted header in the main column; returns the unregister. */
  registerHeader: () => () => void;
};

/**
 * Marks a click as a CHOICE (rail item, list row), not a menu or popover
 * trigger: the narrow-viewport sheet closes on the former only.
 */
export const INBOX_CHOICE_ATTR = "data-inbox-choice";

const InboxShellContext = createContext<InboxShellContextValue | null>(null);

/** Whether the nearest shell is narrow enough to have folded its rail into a sheet. */
export function useInboxShellRailInSheet(): boolean {
  return useContext(InboxShellContext)?.railInSheet ?? false;
}

/**
 * The two-column frame. `detail` REPLACES the list when present rather than
 * sitting beside it — the same behaviour `/emails` has always had, and what
 * makes the shell usable in a narrow dialog (the Entra tester) as well as
 * full-page.
 */
export function InboxShell({
  rail,
  railLabel = "Navigation",
  list,
  detail,
  overlay,
  className,
}: {
  /**
   * Left navigation rail — normally an {@link InboxRail}. `null` means this
   * state has no rail: no sheet, no trigger (a pane with its own back button).
   */
  rail: ReactNode;
  /** What the rail holds ("Mailboxes"): names the narrow-viewport sheet and its button. */
  railLabel?: string;
  /** The list column: typically {@link InboxListHeader}, a list, and a footer. */
  list: ReactNode;
  /** When set, shown INSTEAD of `list`. */
  detail?: ReactNode;
  /** Absolutely-positioned sibling for drop targets and the like. */
  overlay?: ReactNode;
  className?: string;
}) {
  const railInSheet = useIsMobile();
  const [railOpen, setRailOpen] = useState(false);
  // Triggers mounted in the main column. With none (a canvas surface) the
  // shell draws its own, or the rail is unreachable on a phone.
  const [headerCount, setHeaderCount] = useState(0);
  const showingDetail = detail !== undefined && detail !== null;
  const hasRail = rail !== null && rail !== undefined;

  // Picking a rail item swaps the list (or opens a row); the sheet has done
  // its job and would otherwise sit over the very thing that was chosen.
  useEffect(() => {
    setRailOpen(false);
  }, [showingDetail, railInSheet]);

  const openRail = useCallback(() => setRailOpen(true), []);
  const registerHeader = useCallback(() => {
    setHeaderCount((n) => n + 1);
    return () => setHeaderCount((n) => n - 1);
  }, []);
  // Stable identity matters: a header re-registers when this changes, and a
  // fresh object every render would make that registration loop.
  const context = useMemo<InboxShellContextValue>(
    () => ({ railInSheet, inRail: false, railLabel, openRail, registerHeader }),
    [railInSheet, railLabel, openRail, registerHeader],
  );
  const railContext = useMemo(() => ({ ...context, inRail: true }), [context]);

  return (
    <InboxShellContext.Provider value={context}>
      <div className={cn("relative flex h-full min-h-0 min-w-0 flex-1", className)}>
        {!hasRail ? null : railInSheet ? (
          <Sheet open={railOpen} onOpenChange={setRailOpen}>
            <SheetContent
              side="left"
              hideCloseButton
              // The rail may be a hand-rolled column with its own width and
              // border, so the sheet sizes its child rather than trusting it.
              className="w-[min(20rem,85vw)] gap-0 p-0 [&>*]:min-h-0 [&>*]:w-full [&>*]:min-w-0 [&>*]:flex-1 [&>*]:border-r-0"
              onClick={(event) => {
                if (
                  event.target instanceof Element &&
                  event.target.closest(`[${INBOX_CHOICE_ATTR}]`)
                ) {
                  setRailOpen(false);
                }
              }}
            >
              <SheetHeader className="sr-only">
                <SheetTitle>{railLabel}</SheetTitle>
                <SheetDescription>Choose where to look.</SheetDescription>
              </SheetHeader>
              <InboxShellContext.Provider value={railContext}>{rail}</InboxShellContext.Provider>
            </SheetContent>
          </Sheet>
        ) : (
          rail
        )}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">{detail ?? list}</div>
        {hasRail && railInSheet && headerCount === 0 ? (
          <InboxRailTrigger className="absolute top-2 left-2 z-10 bg-background/90 shadow-sm" />
        ) : null}
        {overlay}
      </div>
    </InboxShellContext.Provider>
  );
}

/** The button that opens the folded rail. Renders nothing beside a visible rail. */
function InboxRailTrigger({ className }: { className?: string }) {
  const shell = useContext(InboxShellContext);
  if (!shell?.railInSheet || shell.inRail) return null;
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={cn("size-8 shrink-0 text-muted-foreground", className)}
      aria-label={`Open ${shell.railLabel.toLowerCase()}`}
      onClick={shell.openRail}
    >
      <Inbox className="size-4" />
    </Button>
  );
}

/**
 * The rail container: fixed width, its own scroll, a titled header, and a
 * padded stack for `RailItem` children.
 *
 * `RailItem` deliberately left the container to each surface, on the grounds
 * that widths and headers legitimately differ. In practice the two mail rails
 * agreed on all of it and drifted only by accident, so the container lives
 * here now and the ITEMS are what each surface supplies — mailboxes and their
 * Inbox/Spam/Outbox children on `/emails`, remote folders on the browser.
 */
export function InboxRail({
  title,
  label,
  trailing,
  width = 260,
  children,
  className,
}: {
  /** Header text. Omit for a rail with no header bar (a dialog, say). */
  title?: ReactNode;
  /** `aria-label` for the nav landmark. Falls back to `title` when it is a string. */
  label?: string;
  /**
   * Action rendered at the header's trailing edge, opposite the title — for a
   * control that acts on the whole surface rather than the open item (Compose
   * on `/emails`). Requires `title`; without a header there is nowhere to put it.
   */
  trailing?: ReactNode;
  /** Rail width in px, or any CSS width. */
  width?: number | string;
  children: ReactNode;
  className?: string;
}) {
  const resolvedLabel = label ?? (typeof title === "string" ? title : undefined);
  const railInSheet = useInboxShellRailInSheet();
  return (
    <nav
      {...(resolvedLabel ? { "aria-label": resolvedLabel } : {})}
      className={cn(
        "flex shrink-0 flex-col overflow-y-auto",
        railInSheet ? "w-full" : "border-r border-border",
        className,
      )}
      style={railInSheet ? undefined : { width: typeof width === "number" ? `${width}px` : width }}
    >
      {title !== undefined ? (
        <div className="flex h-14 shrink-0 items-center justify-between gap-2 border-b border-border px-4">
          <span className="text-sm font-semibold">{title}</span>
          {trailing}
        </div>
      ) : null}
      <div className="flex flex-col gap-0.5 p-2">{children}</div>
    </nav>
  );
}

/**
 * The bar above the list or detail: a leading region (title, back button,
 * search) and trailing actions. Fixed height so the rail header and this one
 * line up across the fold.
 */
export function InboxListHeader({
  leading,
  trailing,
  className,
}: {
  leading?: ReactNode;
  trailing?: ReactNode;
  className?: string;
}) {
  const shell = useContext(InboxShellContext);
  const hostsTrigger = Boolean(shell?.railInSheet && !shell.inRail);
  useEffect(() => {
    if (!hostsTrigger) return;
    return shell?.registerHeader();
  }, [hostsTrigger, shell]);
  return (
    <div
      className={cn(
        "flex h-14 shrink-0 items-center justify-between gap-2 border-b border-border px-3 md:px-4",
        className,
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <InboxRailTrigger />
        {leading}
      </div>
      {trailing ? <div className="flex shrink-0 items-center gap-2">{trailing}</div> : null}
    </div>
  );
}

/**
 * A secondary row under {@link InboxListHeader}, for state the header row has
 * no space for: applied filter chips, a counts strip, a scope picker. Unlike
 * the header it has no fixed height, so it wraps with its content and
 * disappears entirely when there is nothing to show.
 *
 * It exists here rather than in a route because the alternative — each surface
 * hand-rolling a second bar under the header — is how the header row itself
 * drifted before the shell owned it.
 */
export function InboxListSubheader({
  children,
  className,
}: {
  children?: ReactNode;
  className?: string;
}) {
  if (!children) return null;
  return (
    <div
      className={cn(
        "flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-border px-4 py-1.5",
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * The scrolling body of the list column. THIS is the scroll boundary — see the
 * note at the top of the file.
 */
export function InboxListBody({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn("relative min-h-0 flex-1 overflow-y-auto", className)}>{children}</div>;
}

/**
 * Pages the list as the reader nears its end. Render it LAST inside
 * {@link InboxListBody}, the scroll container, so entering view means "bottom".
 */
export function InboxListSentinel({
  hasMore,
  loading,
  onLoadMore,
}: {
  hasMore: boolean;
  loading: boolean;
  onLoadMore: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  // Read through a ref so the observer is not rebuilt on every fetch state
  // change; the callback is what varies, the element does not.
  const latest = useRef({ hasMore, loading, onLoadMore });
  latest.current = { hasMore, loading, onLoadMore };

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        const { hasMore, loading, onLoadMore } = latest.current;
        if (hasMore && !loading) onLoadMore();
      },
      { rootMargin: "0px 0px 400px 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} aria-live="polite" className="flex h-10 shrink-0 items-center justify-center">
      {loading ? <span className="text-xs text-muted-foreground">Loading more…</span> : null}
    </div>
  );
}

/** The strip under the list: a cursor pager, a result count. */
export function InboxListFooter({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex shrink-0 flex-col gap-1 border-t border-border px-2 py-2", className)}>
      {children}
    </div>
  );
}

/**
 * A clickable row in the list: the `<li>`, the ghost button, and the selected
 * wash. `leading` and `trailing` are both rendered OUTSIDE the button (a
 * bulk-select checkbox must not open the message it selects, and a per-row
 * action button must not nest inside the row's own `<button>` — nested
 * interactive elements are invalid HTML and invisible to screen readers),
 * which is why this is a component and not a class string the callers share.
 */
export function InboxListItem({
  selected = false,
  selectedTone = "subtle",
  cursor = false,
  elementRef,
  leading,
  trailing,
  onClick,
  ariaLabel,
  children,
}: {
  selected?: boolean;
  /**
   * How loudly the selected row reads. `"subtle"` is the mail idiom, where
   * selection is a bookmark in a list you scroll past. `"strong"` is for a
   * list/detail pair where the row IS the thing on screen beside it and a
   * faint wash leaves the reader hunting for their place: an accent rail plus
   * a filled accent background, the same pair the task inbox uses.
   */
  selectedTone?: "subtle" | "strong";
  /**
   * The keyboard cursor rests here (`j`/`k`). Deliberately a DIFFERENT mark
   * from `selected`: in a mail list "where the keyboard is" and "what a bulk
   * action would hit" are two independent states that are routinely on
   * different rows.
   */
  cursor?: boolean;
  /** The row element, so a caller moving the cursor can scroll it into view. */
  elementRef?: Ref<HTMLLIElement>;
  /** Rendered beside the button, outside its click target. */
  leading?: ReactNode;
  /** Rendered at the row's trailing edge, outside its click target. */
  trailing?: ReactNode;
  onClick: () => void;
  ariaLabel?: string;
  children: ReactNode;
}) {
  return (
    <li
      ref={elementRef}
      // An inset ::before, not the row background, so the mark rounds inside the dividers and covers the checkbox.
      className={cn(
        "relative isolate flex items-stretch border-b border-border/40",
        "before:pointer-events-none before:absolute before:inset-x-1.5 before:inset-y-1 before:-z-10 before:rounded-md before:transition-colors before:duration-150 before:content-['']",
        "hover:before:bg-muted/50 has-[button:focus-visible]:before:ring-[3px] has-[button:focus-visible]:before:ring-ring/50",
        selected && selectedTone === "subtle" && "before:bg-muted/50",
        cursor && "before:bg-muted hover:before:bg-muted",
      )}
    >
      {selected && selectedTone === "strong" ? (
        <span aria-hidden="true" className="absolute inset-y-0 left-0 w-[3px] bg-primary" />
      ) : null}
      {leading ? (
        // A label so the whole padded cell toggles the checkbox, and lends it its hover look.
        <label
          className="flex items-start pt-3 pr-2 pl-4 [&:hover_[data-slot=checkbox][data-state=unchecked]]:border-muted-foreground [&:hover_[data-slot=checkbox]:not([data-state=unchecked])]:bg-primary/90"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          {leading}
        </label>
      ) : null}
      <Button
        type="button"
        variant="ghost"
        size="row"
        {...(ariaLabel ? { "aria-label": ariaLabel } : {})}
        // Rows are buttons, so a click leaves focus inside one; the scope marks
        // this as a row for list shortcuts that must keep working there.
        data-keyboard-shortcut-scope={INBOX_LAYOUT_ROW_KEYBOARD_SCOPE}
        {...{ [INBOX_CHOICE_ATTR]: "" }}
        className={cn(
          "min-w-0 flex-1 items-start rounded-none py-2.5 text-left hover:bg-transparent dark:hover:bg-transparent",
          // The strong fill is opaque and hides the row's ::before focus ring, so it keeps its own.
          selected && selectedTone === "strong"
            ? "bg-accent text-accent-foreground hover:bg-accent"
            : "focus-visible:ring-0",
          leading ? "pl-1" : "pl-4",
          trailing ? "pr-1" : "pr-4",
        )}
        onClick={onClick}
      >
        {children}
      </Button>
      {/*
        No `stopPropagation`: this is a SIBLING of the row's button, not a
        descendant, and the `<li>` carries no handler — there is nothing to stop.
        Swallowing keydown here would instead break any list-level keyboard
        handling (roving focus, typeahead, Escape) added later, but only while
        focus sits in the trailing slot.
      */}
      {trailing ? <div className="flex items-center px-3">{trailing}</div> : null}
    </li>
  );
}

/**
 * One row's inner content, in the three-line mail idiom every list in the
 * product already used independently: who it is from with a timestamp floated
 * right, then the subject, then a muted snippet.
 *
 * Takes rendered pieces rather than a record, because the three callers
 * disagree about what "from" means — a thread's participant list, a stored
 * message's sender, a remote message's display name — and agreeing on the
 * LAYOUT is the point, not agreeing on a schema.
 */
export function InboxRow({
  primary,
  primaryTrailing,
  secondary,
  secondaryTrailing,
  snippet,
  footer,
  tone = "default",
}: {
  /** Line 1, left: usually the sender or participants. */
  primary: ReactNode;
  /** Line 1, right: timestamp, attachment glyph, status badge. */
  primaryTrailing?: ReactNode;
  /** Line 2, left: the subject. */
  secondary?: ReactNode;
  /** Line 2, right: a muted tag (owning app, source folder). */
  secondaryTrailing?: ReactNode;
  /** Line 3: a muted body preview. Omitted when the provider gives none. */
  snippet?: ReactNode;
  /** Line 4: chips and other metadata that wrap (tags). */
  footer?: ReactNode;
  /** `error` reddens the primary line, for a message that failed to process. */
  tone?: "default" | "error";
}) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-0.5 text-left">
      <span className="flex items-baseline justify-between gap-2">
        <span
          className={cn(
            "min-w-0 truncate text-[13.5px] font-semibold",
            tone === "error" ? "text-destructive" : "text-foreground",
          )}
        >
          {primary}
        </span>
        {primaryTrailing ? (
          <span className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
            {primaryTrailing}
          </span>
        ) : null}
      </span>
      {secondary !== undefined || secondaryTrailing !== undefined ? (
        <span className="flex items-baseline justify-between gap-2">
          <span className="min-w-0 truncate text-[13px] text-foreground">{secondary}</span>
          {secondaryTrailing ?? null}
        </span>
      ) : null}
      {snippet ? <span className="truncate text-xs text-muted-foreground">{snippet}</span> : null}
      {footer ? (
        <span className="mt-1 flex min-w-0 flex-wrap items-center gap-1">{footer}</span>
      ) : null}
    </div>
  );
}
